// ONE exercise-catalog loader for every surface (anti-monster S-C).
// Until 2026-06-10 the paged fetch existed twice — web-rnw's useCatalog
// and mobile's exercisesCache — and the 2026-06-09 pagination fix had
// to be applied to BOTH (the classic drift signal). This module owns
// the fetch + validation + ordering; platforms keep only their storage
// adapter (AsyncStorage on mobile, none on web-rnw).

import type { ApiGet } from "./apiFetch.js";
import { paginate } from "./asyncList.js";
import { type Locale } from "./locale.js";
import { pickLocalizedText } from "./localizedText.js";
import { Exercise, type Exercise as ExerciseT } from "./schemas/exercise.js";

/** Minimal GET contract — each app injects its Cognito-token fetch. */
export type CatalogApiFetch = ApiGet;

/**
 * Fetch the COMPLETE catalog (Track 0 paged contract: ?limit opts into
 * {items, cursor}; paginate() walks every page — the legacy bare-array
 * read stopped at DDB's first ≤1MB page). Each row is zod-validated so
 * one malformed item is dropped instead of poisoning consumers, and the
 * result carries the canonical EN-name order every surface expects.
 */
export async function fetchCatalogExercises(
  apiFetch: CatalogApiFetch,
): Promise<ExerciseT[]> {
  const { items: rawItems } = await paginate<unknown>((cursor) =>
    apiFetch<{ items?: unknown[]; cursor?: unknown }>(
      `/api/exercises?limit=500${
        cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""
      }`,
    ).then((page) => ({
      items: Array.isArray(page?.items) ? page.items : [],
      // Coerce a malformed cursor to null so a server-side shape bug can
      // never spin the page loop to paginate()'s max-pages cap.
      cursor: typeof page?.cursor === "string" ? page.cursor : null,
    })),
  );
  const items: ExerciseT[] = [];
  for (const raw of rawItems) {
    const parsed = Exercise.safeParse(raw);
    if (parsed.success) items.push(parsed.data);
  }
  items.sort((a, b) => a.name.en.localeCompare(b.name.en));
  return items;
}

/** Resolve catalog rows to the {slug, name} pairs pickers want
 *  (@jackedlogs/ui CatalogItem is this exact structural shape). Input
 *  order is preserved — fetchCatalogExercises already sorted by the
 *  stable EN canonical, so locale switches don't reshuffle the list. */
export const toCatalogItems = (
  items: ReadonlyArray<ExerciseT>,
  locale: Locale,
): Array<{ slug: string; name: string }> =>
  items.map((ex) => ({
    slug: ex.slug,
    name: pickLocalizedText(ex.name, locale),
  }));
